import { useQuery } from '@tanstack/react-query'
import { Activity, DollarSign, Filter, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { ComparisonChart } from '../components/charts/ComparisonChart'
import { HeatmapChart } from '../components/charts/HeatmapChart'
import { RealtimeChart } from '../components/charts/RealtimeChart'
import { publisherApi } from '../lib/api'

export function Analytics() {
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all')
  const [dateRange, setDateRange] = useState<string>('7d')

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => publisherApi.getCampaigns(),
  })

  const { data: stats } = useQuery({
    queryKey: ['statistics', dateRange],
    queryFn: () => publisherApi.getStatistics(dateRange as '24h' | '7d' | '30d' | '90d'),
  })

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advanced Analytics</h1>
          <p className="text-gray-600 mt-1">Deep insights into your traffic and performance</p>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-3">
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">All Campaigns</option>
            {campaigns?.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>

          <button type="button" className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium bg-white/20 px-2 py-1 rounded">Live</span>
          </div>
          <p className="text-3xl font-bold">{stats?.totalClicks.toLocaleString() || '0'}</p>
          <p className="text-blue-100 mt-1">Total Clicks</p>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium bg-white/20 px-2 py-1 rounded">+12%</span>
          </div>
          <p className="text-3xl font-bold">{stats?.conversions.toLocaleString() || '0'}</p>
          <p className="text-green-100 mt-1">Conversions</p>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium bg-white/20 px-2 py-1 rounded">+8%</span>
          </div>
          <p className="text-3xl font-bold">${stats?.earnings.toFixed(2) || '0.00'}</p>
          <p className="text-purple-100 mt-1">Revenue</p>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium bg-white/20 px-2 py-1 rounded">Good</span>
          </div>
          <p className="text-3xl font-bold">{stats?.conversionRate.toFixed(2) || '0'}%</p>
          <p className="text-orange-100 mt-1">Conversion Rate</p>
        </div>
      </div>

      {/* Real-time Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RealtimeChart title="Live Clicks" color="#3b82f6" />
        <RealtimeChart title="Live Conversions" color="#10b981" />
        <RealtimeChart title="Live Revenue" color="#f59e0b" />
      </div>

      {/* Activity Heatmap */}
      <HeatmapChart title="Weekly Activity Pattern" />

      {/* Campaign Comparison */}
      <ComparisonChart
        title="Campaign Performance Comparison"
        labelA="This Week"
        labelB="Last Week"
      />

      {/* Geographic Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Map placeholder */}
          <div className="bg-gray-50 rounded-lg h-64 flex items-center justify-center">
            <p className="text-gray-500">Interactive map visualization</p>
          </div>

          {/* Country list */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Top Countries</h4>
            {stats?.topCountries?.slice(0, 8).map((country, idx) => (
              <div key={country.country} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-500 w-8">#{idx + 1}</span>
                  <span className="text-sm font-medium text-gray-900">{country.country}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {country.clicks.toLocaleString()} clicks
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    ${country.earnings.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
        <div className="space-y-4">
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Impressions</span>
              <span className="text-sm text-gray-900 font-semibold">10,000</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-8">
              <div className="bg-blue-500 h-8 rounded-full" style={{ width: '100%' }} />
            </div>
          </div>

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Clicks</span>
              <span className="text-sm text-gray-900 font-semibold">{stats?.totalClicks || 0}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-8">
              <div className="bg-indigo-500 h-8 rounded-full" style={{ width: '75%' }} />
            </div>
          </div>

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Conversions</span>
              <span className="text-sm text-gray-900 font-semibold">{stats?.conversions || 0}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-8">
              <div className="bg-green-500 h-8 rounded-full" style={{ width: '25%' }} />
            </div>
          </div>

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Revenue</span>
              <span className="text-sm text-gray-900 font-semibold">
                ${stats?.earnings.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-8">
              <div className="bg-purple-500 h-8 rounded-full" style={{ width: '15%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Best Performing Day</p>
            <p className="text-xl font-bold text-gray-900">Wednesday</p>
            <p className="text-xs text-gray-500 mt-1">32% higher than average</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Peak Hour</p>
            <p className="text-xl font-bold text-gray-900">2:00 PM - 3:00 PM</p>
            <p className="text-xs text-gray-500 mt-1">45% of daily conversions</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Top Device</p>
            <p className="text-xl font-bold text-gray-900">Mobile (iOS)</p>
            <p className="text-xs text-gray-500 mt-1">65% of total traffic</p>
          </div>
        </div>
      </div>
    </div>
  )
}
